/**
 * Builds a prefixed error message by combining prefix and base text.
 *
 * Combines two message parts into a single formatted string with semicolon
 * separation. Returns undefined when both parts are empty or non-string values.
 *
 * @param prefix - Optional prefix text to prepend to the message
 * @param base - Optional base message text
 * @returns Formatted message string or undefined if both inputs are empty
 */
export function formatPrefixedMessage(
	prefix?: unknown,
	base?: unknown
): string | undefined {
	const p = typeof prefix === 'string' ? prefix.trim() : ''
	const b = typeof base === 'string' ? base.trim() : ''
	if (p && b) return `${p}; ${b}`
	if (p) return `${p};`
	if (b) return b
	return undefined
}

/**
 * Normalizes any thrown value into a proper Error instance.
 *
 * Ensures consistent error handling by converting any thrown value into a
 * standardized Error object. Preserves original error properties (name, stack,
 * cause) when the input is already an Error instance.
 *
 * @param error - The error value to normalize (can be any type)
 * @param errorText - Additional error context text
 * @param fallbackMessage - Message to use if error has no message
 * @param unknownLabel - Label prefix for non-Error values (default: 'UNKNOWN')
 * @returns Normalized Error instance with preserved metadata
 */
function normalizeError(
	error: unknown,
	errorText?: string,
	fallbackMessage?: string,
	unknownLabel = 'UNKNOWN'
): Error {
	if (error instanceof Error) {
		const wrapped = new Error(
			error.message ?? errorText ?? fallbackMessage ?? unknownLabel
		)
		wrapped.name = error.name
		wrapped.stack = error.stack
		wrapped.cause = error.cause
		return wrapped
	}

	return new Error(`An ${unknownLabel} error: ${String(error)}, ${errorText}`)
}

/**
 * Normalizes and re-throws any error value as a proper Error instance.
 *
 * Utility function for catch blocks that ensures consistent error handling by
 * converting any thrown value into a standardized Error before re-throwing.
 * Preserves original error metadata when applicable.
 *
 * @param error - The error value to normalize and throw
 * @param errorText - Additional error context text
 * @param fallbackMessage - Message to use if error has no message
 * @param unknownLabel - Label prefix for non-Error values (default: 'UNKNOWN')
 * @throws Always throws a normalized Error instance
 */
export function assertErrorOrThrowUnknown(
	error: unknown,
	errorText?: string,
	fallbackMessage?: string,
	unknownLabel = 'UNKNOWN'
): never {
	// Always throw a normalized Error (preserves previous behavior)
	throw normalizeError(error, errorText, fallbackMessage, unknownLabel)
}

/**
 * Copies text to the system clipboard using the Clipboard API.
 *
 * Provides safe clipboard access with proper error handling and environment
 * checks. Returns success status without throwing errors.
 *
 * @param text - Text to copy to clipboard (undefined returns false immediately)
 * @returns Promise resolving to true if copy succeeded, false otherwise
 */
export async function copyToClipboard(text?: string): Promise<boolean> {
	// Return false early only when undefined; empty string is a valid clipboard payload
	if (text === undefined) return false

	// Guard for environments without Clipboard API
	if (!navigator?.clipboard?.writeText) {
		return false
	}

	try {
		await navigator.clipboard.writeText(text)
		return true
	} catch (err) {
		console.error('Failed to copy to clipboard', err)
		return false
	}
}

/**
 * Extracts and normalizes an error value without throwing.
 *
 * Similar to {@link assertErrorOrThrowUnknown} but returns the normalized Error
 * instead of throwing it. Useful for error handling scenarios where you need
 * to process or log the error before deciding whether to throw.
 *
 * @param error - The error value to normalize
 * @param errorText - Additional error context text
 * @param fallbackMessage - Message to use if error has no message
 * @param unknownLabel - Label prefix for non-Error values (default: 'UNKNOWN')
 * @returns Normalized Error instance with preserved metadata
 */
export function extractErrorOrUnknown(
	error: unknown,
	errorText?: string,
	fallbackMessage?: string,
	unknownLabel = 'UNKNOWN'
): Error {
	return normalizeError(error, errorText, fallbackMessage, unknownLabel)
}
